/**
 * workshopLeadTime — the date rules for creating a workshop (BON-203, BON-508).
 *
 * Two rules, deliberately different in strength:
 *   - PAST dates are refused outright (the API refuses them too).
 *   - The 6-week lead only WARNS. It is a scheduling nudge, not an invariant:
 *     near-term workshops are legitimate and used to be uncreatable (BON-508).
 *     The API stopped enforcing it, matching the publish gate (BON-469).
 *
 * ⚠️ The operator is unsure 6 weeks is hard-and-fast — keep this a single
 * source so the threshold is trivial to change/relax later. It is now a
 * CLIENT-ONLY value: no server constant mirrors it any more.
 */
import { viewerDate } from '../../../../utils/viewer-date';

export const MIN_WORKSHOP_LEAD_DAYS = 42;

export const MIN_LEAD_MESSAGE = 'Workshop should start at least 6 weeks from today.';

export const PAST_DATE_MESSAGE = 'Workshop cannot start in the past.';

/** YYYY-MM-DD for today on the operator's own calendar — the date picker's
 *  `min`, and the past-date cut. Not UTC: in the evening UTC is already
 *  tomorrow, which used to refuse today's date across the US. */
export function earliestWorkshopDate(now: Date = new Date()): string {
  return viewerDate(0, now);
}

/** YYYY-MM-DD for the date the 6-week lead notice stops applying. */
export function minWorkshopDate(now: Date = new Date()): string {
  return viewerDate(MIN_WORKSHOP_LEAD_DAYS, now);
}

/** True when `date` (YYYY-MM-DD) is before today. Day-granular: a workshop
 *  LATER today passes here, and the API — which compares full instants — has
 *  the final say if the time of day has already gone by. */
export function isPastDate(date: string, now: Date = new Date()): boolean {
  return date < earliestWorkshopDate(now);
}

/** True when `date` (YYYY-MM-DD) is on or after the 6-week lead date. */
export function meetsLeadTime(date: string, now: Date = new Date()): boolean {
  return date >= minWorkshopDate(now);
}
